import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { IFRAME_ALLOWED_ORIGINS } from '../constants/api';
import { IFRAME_PREVIEW_CAMPAIGN_ID } from '../constants/http';
import { ROUTE_NAMES, ROUTE_TEMPLATES } from '../constants/routes';
import { submitMSKUStep, submitPickYourGiftStep } from '../store/actions/form';
import { getFormDataSuccessAction, loadIframeInitDataAction, setIframeExperienceAction } from '../store/actions/init';
import { setKeysAction } from '../store/actions/keys';
import { selectCampaigns, selectItems } from '../store/selectors/init';
import { IframeEventsEnum, IframeInboundMessage } from '../types/iframe';
import { ClaimPageStepEnum } from '../types/shell';
import { IInitSuccessPayload } from '../types/store';
import { buildPath } from '../util/buildPath';
import { getAutoPickedItems } from '../util/helpers';

// True when this document is rendered inside another window (i.e. an iframe).
const isFramed = (): boolean => {
  try {
    return window.parent !== null && window.parent !== window;
  } catch {
    // Cross-origin access to window.parent can throw; if it does, we are framed.
    return true;
  }
};

// The parent window we post messages back to.
const getParentTarget = (): Window | null => {
  const target = window.opener && window.opener !== window ? window.opener : window.parent;

  return target && target !== window ? target : null;
};

export const useIframe = () => {
  const dispatch = useDispatch();
  const campaigns = useSelector(selectCampaigns);
  const items = useSelector(selectItems);
  const history = useHistory();
  const { pathname } = useLocation();

  // Are we the preview target? Independent of Redux/saga state so the handshake
  // can run even if the init saga bailed out (e.g. missing document.referrer on
  // a cached, client-side navigation from the builder's list). The whole point
  // is that the preview is driven purely by postMessage — never by a fetch or a
  // referrer check.
  const isIframeRoute = pathname === ROUTE_NAMES.iframe || pathname.startsWith(`/o/${IFRAME_PREVIEW_CAMPAIGN_ID}/`);
  const isPreviewTarget = isIframeRoute && isFramed();

  const submitPYG = useCallback((): Promise<string> => {
    const lastCampaign = campaigns?.[campaigns.length - 1];
    if (!lastCampaign) {
      return Promise.resolve('');
    }

    return new Promise<string>((resolve) => {
      dispatch(submitPickYourGiftStep({ campaignId: lastCampaign.id, resolve }));
    });
  }, [dispatch, campaigns]);

  const submitMSKU = useCallback((): Promise<string> => {
    if (!items) {
      return Promise.resolve('');
    }

    const autoPicked = getAutoPickedItems(items);
    if (!Object.keys(autoPicked).length) {
      return Promise.resolve('');
    }

    return new Promise<string>((resolve) => {
      dispatch(submitMSKUStep({ pickedItems: autoPicked, resolve }));
    });
  }, [dispatch, items]);

  // Apply the full preview payload. Sets up embed mode locally (no saga, no
  // fetch) so the preview renders purely from what the builder sent. Idempotent:
  // the builder re-sends `init` on every reconnect, and re-applying just
  // overwrites state.
  const applyInit = useCallback(
    (payload: { data?: IInitSuccessPayload; page?: ClaimPageStepEnum } | undefined) => {
      const { data: initData, page } = payload || {};

      dispatch(setKeysAction({ one_link_id: IFRAME_PREVIEW_CAMPAIGN_ID }));
      dispatch(loadIframeInitDataAction());

      if (initData) {
        dispatch(getFormDataSuccessAction(initData));
      }

      history.replace(
        buildPath(ROUTE_TEMPLATES.ONE_LINK, {
          oneLinkId: IFRAME_PREVIEW_CAMPAIGN_ID,
          step: page || ClaimPageStepEnum.PickYourGift,
        }),
      );
    },
    [dispatch, history],
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!IFRAME_ALLOWED_ORIGINS.includes(event.origin)) {
        return;
      }

      const data = event.data as IframeInboundMessage;
      if (!data || typeof data !== 'object') {
        return;
      }

      // Reply to every ping. This is the authoritative handshake: whichever side
      // attaches its listener last, the next ping/reply pair recovers it.
      if (data.type === IframeEventsEnum.Ping) {
        const source = event.source as Window | null;
        source?.postMessage({ type: IframeEventsEnum.Ready }, event.origin);

        return;
      }

      if (data.type === IframeEventsEnum.Init) {
        applyInit(data.payload);

        return;
      }

      if (data.type === IframeEventsEnum.SetExperience && !!data.payload?.experience) {
        dispatch(setIframeExperienceAction(data.payload.experience));

        return;
      }

      if (data.type === IframeEventsEnum.Navigate && !!data.payload?.step) {
        const { step } = data.payload;

        const navigate = () =>
          history.push(buildPath(ROUTE_TEMPLATES.ONE_LINK, { oneLinkId: IFRAME_PREVIEW_CAMPAIGN_ID, step }));

        const isPickMskuStep = step === ClaimPageStepEnum.CustomizeOptions;
        const isAddressStep = step === ClaimPageStepEnum.Address && !!items;

        if (isPickMskuStep) {
          submitPYG().then(navigate);

          return;
        }

        if (isAddressStep) {
          submitMSKU().then(navigate);

          return;
        }

        navigate();
      }
    },
    [dispatch, history, items, submitPYG, submitMSKU, applyInit],
  );

  // Broadcast readiness on mount, repeated, to cover the builder attaching its
  // listener slightly after the (possibly cached, instant) iframe load. Payload
  // free, so it is safe to post to '*'. The ping reply above is the targeted,
  // authoritative signal; this is the mount-timing safety net.
  const readyTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isPreviewTarget) {
      return undefined;
    }

    window.addEventListener('message', handleMessage);

    const announceReady = () => {
      getParentTarget()?.postMessage({ type: IframeEventsEnum.Ready }, '*');
    };

    announceReady();

    let readyTicks = 0;
    readyTimerRef.current = window.setInterval(() => {
      announceReady();
      readyTicks += 1;
      if (readyTicks >= 10 && readyTimerRef.current !== null) {
        window.clearInterval(readyTimerRef.current);
        readyTimerRef.current = null;
      }
    }, 300);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (readyTimerRef.current !== null) {
        window.clearInterval(readyTimerRef.current);
        readyTimerRef.current = null;
      }
    };
  }, [isPreviewTarget, handleMessage]);
};
